import React from 'react';
import { FileText, TrendingUp, Calendar, BarChart2, Clock, Zap, Target, Layers } from 'lucide-react';
import { MARKET_CODE_TO_DISPLAY } from '../lib/constants';
import { MarketFlag } from '../lib/MarketFlag';
import { useData } from '../contexts/DataContext';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just nu';
  if (m < 60) return `${m} min sedan`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} tim sedan`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Igår';
  return `${d} dagar sedan`;
}

export default function DashboardPage() {
  const { stats } = useData();
  
  const sortedMarkets = Object.entries(stats.byMarket).sort((a, b) => b[1] - a[1]);
  const maxMarket = sortedMarkets[0]?.[1] || 1;
  
  const sortedCategories = Object.entries(stats.byCategory).sort((a, b) => b[1] - a[1]);
  const maxCategory = sortedCategories[0]?.[1] || 1;

  const maxDaily = Math.max(...stats.dailyBreakdown.map(d => d.count), 1);

  return (
    <div className="page-scroll">
      <div className="page-header">
        <div>
          <h1 className="page-title">Översikt</h1>
          <p className="page-subtitle">Din aktivitet och produktionsstatus</p>
        </div>
        <div className="dash-header-stats">
          <span className="dash-live-dot" />
          <span className="dash-live-text">Senast uppdaterad just nu</span>
        </div>
      </div>

      {/* Primary Stat Cards */}
      <div className="stats-grid stats-grid--4">
        <div className="stat-card stat-card--highlight">
          <div className="stat-card-icon stat-card-icon--blue">
            <Calendar size={20} />
          </div>
          <div className="stat-card-body">
            <p className="stat-label">Idag</p>
            <p className="stat-value stat-value--large" data-testid="stat-today">{stats.today}</p>
            <p className="stat-sublabel">beskrivningar</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon stat-card-icon--cyan">
            <TrendingUp size={20} />
          </div>
          <div className="stat-card-body">
            <p className="stat-label">Denna vecka</p>
            <p className="stat-value" data-testid="stat-week">{stats.thisWeek}</p>
            <p className="stat-sublabel">senaste 7 dagar</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon stat-card-icon--emerald">
            <FileText size={20} />
          </div>
          <div className="stat-card-body">
            <p className="stat-label">Totalt</p>
            <p className="stat-value" data-testid="stat-total">{stats.total}</p>
            <p className="stat-sublabel">alla beskrivningar</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon stat-card-icon--violet">
            <Target size={20} />
          </div>
          <div className="stat-card-body">
            <p className="stat-label">Kvalitet</p>
            <p className="stat-value" data-testid="stat-quality">{stats.qualityRate}%</p>
            <p className="stat-sublabel">inom 150-220 ord</p>
          </div>
        </div>
      </div>

      {/* Secondary Stats Row */}
      <div className="stats-grid stats-grid--3">
        <div className="stat-card stat-card--compact">
          <div className="stat-card-icon stat-card-icon--amber stat-card-icon--sm">
            <BarChart2 size={16} />
          </div>
          <div className="stat-card-body">
            <p className="stat-label-sm">Snitt ordantal</p>
            <p className="stat-value-sm">{stats.avgWords || '—'} ord</p>
          </div>
        </div>

        <div className="stat-card stat-card--compact">
          <div className="stat-card-icon stat-card-icon--rose stat-card-icon--sm">
            <Zap size={16} />
          </div>
          <div className="stat-card-body">
            <p className="stat-label-sm">Bulk-genererade</p>
            <p className="stat-value-sm">{stats.bySource.bulk || 0}</p>
          </div>
        </div>

        <div className="stat-card stat-card--compact">
          <div className="stat-card-icon stat-card-icon--sky stat-card-icon--sm">
            <Layers size={16} />
          </div>
          <div className="stat-card-body">
            <p className="stat-label-sm">Kategorier</p>
            <p className="stat-value-sm">{sortedCategories.length}</p>
          </div>
        </div>
      </div>

      {/* Weekly Chart */}
      <div className="dash-card dash-card--wide">
        <h2 className="dash-card-title">Aktivitet senaste 7 dagar</h2>
        <div className="weekly-chart">
          {stats.dailyBreakdown.map((day, i) => (
            <div key={i} className="weekly-bar-col">
              <div className="weekly-bar-wrap">
                <div 
                  className="weekly-bar"
                  style={{ height: `${Math.max((day.count / maxDaily) * 100, 4)}%` }}
                >
                  {day.count > 0 && <span className="weekly-bar-value">{day.count}</span>}
                </div>
              </div>
              <span className="weekly-bar-label">{day.dayName}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Market breakdown */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h2 className="dash-card-title">Per marknad</h2>
            <span className="dash-card-count">{sortedMarkets.length} marknader</span>
          </div>
          {sortedMarkets.length === 0 ? (
            <p className="dash-empty">Inga genereringar ännu</p>
          ) : (
            <div className="market-list">
              {sortedMarkets.map(([market, count]) => (
                <div key={market} className="market-row">
                  <div className="market-row-left">
                    <MarketFlag market={market} />
                    <span className="market-name">{market}</span>
                  </div>
                  <div className="market-bar-wrap">
                    <div
                      className="market-bar"
                      style={{ width: `${Math.round((count / maxMarket) * 100)}%` }}
                    />
                  </div>
                  <span className="market-count">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category breakdown */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h2 className="dash-card-title">Per kategori</h2>
            <span className="dash-card-count">{sortedCategories.length} kategorier</span>
          </div>
          {sortedCategories.length === 0 ? (
            <p className="dash-empty">Inga genereringar ännu</p>
          ) : (
            <div className="market-list">
              {sortedCategories.map(([cat, count]) => (
                <div key={cat} className="market-row">
                  <div className="market-row-left">
                    <span className="cat-icon">📦</span>
                    <span className="market-name">{cat}</span>
                  </div>
                  <div className="market-bar-wrap">
                    <div
                      className="market-bar market-bar--cat"
                      style={{ width: `${Math.round((count / maxCategory) * 100)}%` }}
                    />
                  </div>
                  <span className="market-count">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent generations */}
      <div className="dash-card dash-card--wide">
        <div className="dash-card-header">
          <h2 className="dash-card-title">Senaste genereringar</h2>
          <span className="dash-card-count">{stats.recent.length} senaste</span>
        </div>
        {stats.recent.length === 0 ? (
          <div className="dash-empty-state">
            <FileText size={32} className="dash-empty-icon" />
            <p className="dash-empty-title">Inga genereringar ännu</p>
            <p className="dash-empty-desc">Börja med att generera din första beskrivning under "Generera"</p>
          </div>
        ) : (
          <div className="recent-table-wrap">
            <table className="recent-table">
              <thead>
                <tr>
                  <th>Produkt</th>
                  <th>Kategori</th>
                  <th>Marknad</th>
                  <th>Ordantal</th>
                  <th>Källa</th>
                  <th>Tid</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map((h) => (
                  <tr key={h.id}>
                    <td className="recent-td-product">
                      <span className="recent-product-name">{h.productName}</span>
                    </td>
                    <td className="recent-td-cat">{h.category}</td>
                    <td className="recent-td-market">
                      <MarketFlag market={h.market} />
                      <span>{MARKET_CODE_TO_DISPLAY[h.market] || h.market}</span>
                    </td>
                    <td className="recent-td-wc">
                      {h.wordCount > 0 ? (
                        <span className={`wc-badge ${h.wordCount >= 150 && h.wordCount <= 220 ? 'wc-badge--ok' : 'wc-badge--warn'}`}>
                          {h.wordCount}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="recent-td-source">
                      <span className={`source-badge source-badge--${h.source || 'single'}`}>
                        {h.source === 'bulk' ? 'Bulk' : 'Enskild'}
                      </span>
                    </td>
                    <td className="recent-td-time">
                      <span className="recent-time">{timeAgo(h.timestamp)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
